// src/pages/taxonomy/Colours/ColoursFormPage.jsx
import React, { useEffect, useState } from 'react';
import { useParams, useHistory, Link } from 'react-router-dom';
import { ColoursAPI } from '../../../api/v3/taxonomy-api-v3';

const BASE = '/app/sales/bagspa/taxonomy';

// ensure "#RRGGBB" or empty
function normalizeHex(input) {
  if (!input) return '';
  let v = String(input).trim();
  if (!v.startsWith('#')) v = `#${v}`;
  // expand #RGB to #RRGGBB (optional)
  if (/^#([0-9a-fA-F]{3})$/.test(v)) {
    v = v.replace(
      /^#([0-9a-fA-F])([0-9a-fA-F])([0-9a-fA-F])$/,
      (_, r, g, b) => `#${r}${r}${g}${g}${b}${b}`
    );
  }
  // if it doesn't match 6-hex, leave as-is (user can fix)
  return v;
}

// unwrap different response shapes
function unwrapItem(payload) {
  if (!payload) return null;
  if (payload.item) return payload.item;
  if (payload.data && !Array.isArray(payload.data)) return payload.data;
  if (payload.id) return payload;
  return null;
}

export default function ColoursFormPage() {
  const { id } = useParams();
  const history = useHistory();
  const isEdit = Boolean(id);

  const [name, setName]   = useState('');
  const [hex, setHex]     = useState('');  // text hex
  const [pick, setPick]   = useState('#000000'); // native color picker value
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  // keep text <-> color picker in sync
  useEffect(() => {
    if (!hex) return;
    const nx = normalizeHex(hex);
    if (nx !== pick) setPick(nx);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hex]);

  useEffect(() => {
    if (!pick) return;
    if (pick !== hex) setHex(pick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pick]);

  useEffect(() => {
    async function load() {
      if (!isEdit) return;
      try {
        setLoading(true);
        const data = await ColoursAPI.show(id);
        const item = unwrapItem(data);
        setName(item?.name || '');
        const backendHex = item?.hex_code ?? item?.hex ?? '';
        const nx = normalizeHex(backendHex);
        setHex(nx);
        setPick(nx || '#000000');
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [id, isEdit]);

  async function handleSubmit(e) {
    e.preventDefault();
    try {
      setLoading(true);
      const payload = { name };
      if (hex) payload.hex_code = normalizeHex(hex);
      if (isEdit) {
        await ColoursAPI.update(id, payload);
      } else {
        await ColoursAPI.create(payload);
      }
      history.push(`${BASE}/colours`);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0">{isEdit ? 'Edit' : 'New'} Colour</h3>
        <Link to={`${BASE}/colours`}>&larr; Back</Link>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Colour name</label>
          <input
            className="form-control"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Unique name"
            required
          />
        </div>

        <div className="form-group">
          <label>Hex code (optional)</label>
          <div className="d-flex align-items-center" style={{ gap: 12 }}>
            {/* Native color picker */}
            <input
              type="color"
              value={pick || '#000000'}
              onChange={e => setPick(e.target.value)}
              title="Pick a color"
              style={{ width: 46, height: 38, padding: 0, border: '1px solid #ddd', borderRadius: 4 }}
            />
            {/* Text value (keeps #RRGGBB) */}
            <input
              type="text"
              className="form-control"
              value={hex}
              onChange={e => setHex(e.target.value)}
              placeholder="#RRGGBB"
              style={{ maxWidth: 200 }}
            />
            {/* Quick preview box (helpful if bootstrap not loaded) */}
            <span
              title={hex || 'preview'}
              style={{
                width: 28,
                height: 28,
                borderRadius: 4,
                border: '1px solid #ddd',
                background: hex || 'transparent',
                display: 'inline-block'
              }}
            />
          </div>
          <small className="form-text text-muted">
            Accepts <code>#RRGGBB</code> or <code>RRGGBB</code>. You can type or use the picker.
          </small>
        </div>

        <button className="btn btn-primary" disabled={loading}>
          {loading ? 'Saving…' : 'Save'}
        </button>
      </form>
    </div>
  );
}
